import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Send, Phone, Mail } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [website, setWebsite] = useState(""); // honeypot — humans won't fill this
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Honeypot — silently drop bot submissions
    if (website) {
      toast.success("Your message is on its way to Verna ✦");
      setForm({ name: "", email: "", message: "" });
      setWebsite("");
      return;
    }
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your message is on its way to Verna ✦", {
        description: "We'll be in touch personally within 48 hours.",
      });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email vernajcarr@gmail.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" data-testid="book-section" className="py-24 sm:py-32 bg-gradient-to-b from-white to-[var(--lh-cream)]">
      <div className="container-lh max-w-3xl">
        <div className="text-center mb-10">
          <Heart className="w-9 h-9 text-coral mx-auto mb-3" fill="currentColor" />
          <p className="eyebrow">Reserve Your Place</p>
          <h2 className="font-display text-4xl sm:text-5xl text-wine mt-4">Let's have an honest conversation</h2>
        </div>

        <div className="space-y-5 text-wine text-[1.05rem] sm:text-[1.1rem] leading-[1.85]">
          <p>
            Spending this kind of money on yourself is a big decision — and it should be. That's exactly why there's no form to fill out here. Reach out directly, and we'll have an honest conversation about what you're looking for, what to expect, and whether this is genuinely the right fit for you right now. <em>If it's not, I'll say so.</em>
          </p>
          <p>
            Our retreats are kept small on purpose. Real connection — with yourself, with the people around you, with the land. Whether you're after a meaningful holiday, some long-overdue personal time, a heartfelt understanding of your own spiritual growth, or simply something you can't quite name yet — this is a good place to start.
          </p>
          <p>
            And the Coromandel itself is part of the experience. We include a road trip through some of the most breathtaking landscape in New Zealand — ancient bush, white sand beaches, coastal roads, time to slow down and remember what matters.
          </p>
        </div>

        <p className="mt-10 font-display text-3xl sm:text-4xl text-center" style={{ color: "var(--lh-coral)" }}>
          Reach out to Verna and let's have that conversation.
        </p>

        {/* Direct contact details — large, prominent */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10">
          <a href="mailto:vernajcarr@gmail.com" data-testid="contact-email" className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white border-2 border-[var(--lh-gold)] hover:bg-[var(--lh-gold)] hover:text-white transition-all text-wine font-display text-xl">
            <Mail className="w-6 h-6" /> vernajcarr@gmail.com
          </a>
          <a href="tel:+64273200079" data-testid="contact-phone" className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white border-2 border-[var(--lh-plum)] hover:bg-[var(--lh-plum)] hover:text-white transition-all text-wine font-display text-xl">
            <Phone className="w-6 h-6" /> +64 027 320 0079
          </a>
        </div>

        <p className="mt-3 text-center text-sm uppercase tracking-[0.3em] text-plum">Text or phone Verna · or use the message form below</p>

        {/* Light interactive form */}
        <form onSubmit={handleSubmit} className="soft-card mt-12 p-7 sm:p-10 space-y-5" data-testid="booking-form">
          <h3 className="font-display text-2xl text-wine text-center mb-2">Or drop Verna a quick message here</h3>

          {/* Honeypot — invisible to humans, catches bots */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
            <label htmlFor="website">Website (leave blank)</label>
            <input
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-[0.78rem] uppercase tracking-[0.18em] text-plum font-semibold">Your Name</Label>
              <Input data-testid="booking-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="What should we call you?" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-[0.78rem] uppercase tracking-[0.18em] text-plum font-semibold">Your Email</Label>
              <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@email.com" className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label className="text-[0.78rem] uppercase tracking-[0.18em] text-plum font-semibold">Your Message</Label>
            <Textarea data-testid="booking-message" rows={5} value={form.message} onChange={(e) => setField("message", e.target.value)} placeholder="What's drawing you to the retreat? Anything you'd like to ask?" className="mt-1.5" />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center mt-2" data-testid="booking-submit">
            {submitting ? "Sending..." : "Send My Enquiry"} <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </section>
  );
}
